import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FaFileExcel,
  FaEdit,
  FaSearch,
  FaBoxOpen,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";
import apiRequest from "../services/api";
import { exportStocksToExcel } from "../utils/exportStocksToExcel";
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 1000, 2000, 5000, 10000];

export default function StockPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editError, setEditError] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");

  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [branchFilter, setBranchFilter] = useState("ALL");
  const [stockFilter, setStockFilter] = useState("ALL");
  const [activeFilter, setActiveFilter] = useState("ALL");

  const categories = useMemo(
    () => [...new Set(rows.map((r) => r.categoryName).filter(Boolean))].sort(),
    [rows]
  );

  const branches = useMemo(
    () => [...new Set(rows.map((r) => r.branchName).filter(Boolean))].sort(),
    [rows]
  );

  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);

  const [batchRows, setBatchRows] = useState([]);
  const [batchPage, setBatchPage] = useState(0);
  const [batchSize] = useState(10);
  const [batchTotalPages, setBatchTotalPages] = useState(1);
  const [batchLoading, setBatchLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [stockForm, setStockForm] = useState({
    id: null,
    reorderLevel: 0,
    reservedQuantity: 0,
  });

  const fetchBatches = useCallback(
    async (productId) => {
      if (!productId) return;
      setBatchLoading(true);
      try {
        const params = new URLSearchParams({
          page: batchPage.toString(),
          size: batchSize.toString(),
          productId: productId.toString(),
        });
        const res = await apiRequest(`stocks/batches?${params.toString()}`);
        setBatchRows(res?.data?.content || []);
        setBatchTotalPages(res?.data?.totalPages || 1);
      } catch {
        setBatchRows([]);
      } finally {
        setBatchLoading(false);
      }
    },
    [batchPage, batchSize]
  );

  const available = (r) =>
    Number(r.quantity || 0) - Number(r.reservedQuantity || 0);

  const isLowStock = (r) => available(r) <= Number(r.reorderLevel || 0);

  const [sortConfig, setSortConfig] = useState({
    key: "productName",
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const availableQty = Number(r.availableQuantity ?? available(r));

      const reorder = Number(r.reorderLevel || 0);

      const isLow =
        r.lowStockAlert === true ||
        (availableQty > 0 && availableQty <= reorder);

      const isOutOfStock = availableQty <= 0;

      if (categoryFilter !== "ALL" && r.categoryName !== categoryFilter)
        return false;

      if (branchFilter !== "ALL" && r.branchName !== branchFilter) return false;

      if (activeFilter !== "ALL" && String(r.active) !== activeFilter)
        return false;

      if (stockFilter === "LOW" && (!isLow || isOutOfStock)) return false;

      if (stockFilter === "IN_STOCK" && (isLow || isOutOfStock)) return false;

      if (stockFilter === "OUT_OF_STOCK" && !isOutOfStock) return false;

      return true;
    });
  }, [rows, categoryFilter, branchFilter, stockFilter, activeFilter]);

  const sortedRows = useMemo(() => {
    if (!filteredRows.length) return [];

    return [...filteredRows].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (
        [
          "quantity",
          "reservedQuantity",
          "reorderLevel",
          "averageCostPrice",
          "lastPurchasePrice",
        ].includes(sortConfig.key)
      ) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (sortConfig.key === "available") {
        aValue = Number(a.availableQuantity ?? available(a));
        bValue = Number(b.availableQuantity ?? available(b));
      }

      if (sortConfig.key === "status") {
        aValue = isLowStock(a) ? 0 : 1;
        bValue = isLowStock(b) ? 0 : 1;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;

      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

      return 0;
    });
  }, [filteredRows, sortConfig]);

  // =====================================================
  // PAGE SUMMARY TOTALS (mirrors PaymentManagement style)
  // =====================================================
  const pageTotals = useMemo(() => {
    return sortedRows.reduce(
      (acc, r) => {
        const qty = Number(r.quantity || 0);
        const reserved = Number(r.reservedQuantity || 0);
        const avail = Number(r.availableQuantity ?? available(r));
        const reorder = Number(r.reorderLevel || 0);
        const avgCost = Number(r.averageCostPrice || 0);
        const lastCost = Number(r.lastPurchasePrice || 0);

        const isOut = avail <= 0;
        const isLow =
          r.lowStockAlert === true || (avail > 0 && avail <= reorder);

        acc.totalQty += qty;
        acc.totalReserved += reserved;
        acc.totalAvailable += avail;
        acc.count += 1;

        // Inventory value based on quantity × cost
        acc.valueAtAvgCost += qty * avgCost;
        acc.valueAtLastCost += qty * lastCost;

        if (isOut) acc.outOfStockCount += 1;
        else if (isLow) acc.lowStockCount += 1;
        else acc.inStockCount += 1;

        return acc;
      },
      {
        totalQty: 0,
        totalReserved: 0,
        totalAvailable: 0,
        lowStockCount: 0,
        outOfStockCount: 0,
        inStockCount: 0,
        count: 0,
        valueAtAvgCost: 0,
        valueAtLastCost: 0,
      }
    );
  }, [sortedRows]);

  const formatNumber = (n) =>
    Number(n || 0).toLocaleString("en-ET", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const formatMoney = (n) =>
    Number(n || 0).toLocaleString("en-ET", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-4 py-4 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white transition ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div
          className={`flex items-center gap-1.5 ${
            className.includes("text-right")
              ? "justify-end"
              : className.includes("text-center")
              ? "justify-center"
              : ""
          }`}
        >
          <span>{label}</span>
          <span className="text-[10px] opacity-70">
            {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
          </span>
        </div>
      </th>
    );
  };

  useEffect(() => {
    if (batchModalOpen && selectedStock) {
      fetchBatches(selectedStock.productId);
    }
  }, [batchModalOpen, selectedStock, batchPage, fetchBatches]);

  const fetchStocks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (search) params.append("keyword", search);
      const res = await apiRequest(`stocks?${params.toString()}`);
      setRows(res?.data?.content || []);
      setTotalPages(res?.data?.totalPages || 1);
      setTotalElements(res?.data?.totalElements || 0);
    } catch (err) {
      setError(err?.message || "Unable to load stock data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, search]);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(input.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [input]);

  const handleUpdateStock = async () => {
    setEditError("");
    try {
      await apiRequest(`stocks/update`, {
        method: "PUT",
        body: {
          id: stockForm.id,
          reorderLevel: stockForm.reorderLevel,
          reservedQuantity: stockForm.reservedQuantity,
        },
      });
      setEditModalOpen(false);
      fetchStocks();
    } catch (err) {
      setEditError(err?.message || "Failed to update stock");
    }
  };

  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800
      dark:bg-slate-950 dark:text-slate-100"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <div
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
  bg-white border-slate-200
  dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          {/* LEFT SIDE – title */}
          <div className="flex items-center gap-3.5">
            <div
              className="p-3 rounded-xl shadow-inner border
      bg-gradient-to-br from-amber-100 to-orange-50 border-amber-200 text-amber-600
      dark:from-amber-500/20 dark:to-orange-500/10 dark:border-amber-500/30 dark:text-amber-400"
            >
              <FaBoxOpen className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Stock Management
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                  {totalElements}
                </span>{" "}
                items in inventory
              </p>
            </div>
          </div>

          {/* RIGHT SIDE – Export button */}
          <button
            type="button"
            onClick={() => exportStocksToExcel(rows, "stock_inventory")}
            disabled={!rows.length}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border
      bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300
      dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-700/60
      disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaFileExcel className="text-sm" />
            Export
          </button>
        </div>

        {/* SEARCH */}
        <div
          className="grid grid-cols-1 gap-3 p-3 rounded-xl border backdrop-blur-sm
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search product or branch..."
              className="w-full rounded-xl pl-9 pr-4 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs border bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
            >
              <option value="ALL">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs border bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
            >
              <option value="ALL">All Branches</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>

            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs border bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
            >
              <option value="ALL">All Stock Statuses</option>
              <option value="IN_STOCK">In Stock</option>
              <option value="LOW">Low Stock</option>
              <option value="OUT_OF_STOCK">Out of Stock</option>
            </select>

            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs border bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
            >
              <option value="ALL">All Product States</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-slate-500">
              {sortedRows.length} matching records on this page
            </span>

            <button
              type="button"
              onClick={() => {
                setCategoryFilter("ALL");
                setBranchFilter("ALL");
                setStockFilter("ALL");
                setActiveFilter("ALL");
              }}
              className="text-xs font-semibold text-amber-600 hover:text-amber-700"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1100px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
      bg-slate-50 border-slate-200 text-slate-500
      dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <th className="px-3 py-4 text-center">SN</th>
                  <SortableHeader
                    label="Product"
                    sortKey="productName"
                    className="px-5"
                  />
                  <SortableHeader label="Branch" sortKey="branchName" />
                  <SortableHeader
                    label="Qty"
                    sortKey="quantity"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Reserved"
                    sortKey="reservedQuantity"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Available"
                    sortKey="available"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Reorder"
                    sortKey="reorderLevel"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Avg Cost"
                    sortKey="averageCostPrice"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Last Cost"
                    sortKey="lastPurchasePrice"
                    className="text-right"
                  />
                  <SortableHeader label="Status" sortKey="status" />
                  <th className="px-5 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y font-mono text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {sortedRows.map((r, index) => (
                  <tr
                    key={r.id}
                    onClick={() => {
                      setSelectedStock(r);
                      setBatchPage(0);
                      setBatchModalOpen(true);
                    }}
                    className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02] cursor-pointer"
                  >
                    <td className="px-3 py-3.5 text-center text-slate-400">
                      {page * size + index + 1}
                    </td>
                    <td className="px-5 py-3.5 font-sans">
                      <div className="font-bold tracking-wide text-slate-900 dark:text-white">
                        {r.productName}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {r.productBarcode}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 font-sans">{r.branchName}</td>
                    <td className="px-4 py-3.5 text-right">{r.quantity}</td>
                    <td className="px-4 py-3.5 text-right">
                      {r.reservedQuantity}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-amber-600 dark:text-amber-500">
                      {available(r)}
                    </td>
                    <td className="px-4 py-3.5 text-right">{r.reorderLevel}</td>
                    <td className="px-4 py-3.5 text-right">
                      {r.averageCostPrice}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {r.lastPurchasePrice}
                    </td>
                    <td className="px-4 py-3.5 font-sans">
                      {isLowStock(r) ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20">
                          Low Stock
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                          In Stock
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2 text-center font-sans">
                      <button
                        type="button"
                        title="Edit Stock"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditError("");
                          setStockForm({
                            id: r.stockId,
                            reorderLevel: r.reorderLevel || 0,
                            reservedQuantity: r.reservedQuantity || 0,
                          });
                          setEditModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center p-2 rounded-lg text-xs font-semibold transition border
                          bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600 hover:text-slate-900
                          dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/5 dark:text-slate-300 dark:hover:text-white"
                      >
                        <FaEdit className="text-[12px]" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ========== PAGE SUMMARY (same style as PaymentManagement) ========== */}
            {!loading && sortedRows.length > 0 && (
              <div className="border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-950/40">
                <div className="px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Page summary
                  <span className="ml-2 font-mono font-normal normal-case text-slate-400">
                    ({pageTotals.count} records)
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3 px-5 pb-4 text-xs">
                  {/* Total Quantity */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Total Qty
                    </div>
                    <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                      {formatNumber(pageTotals.totalQty)}
                    </div>
                  </div>

                  {/* Total Reserved */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Reserved
                    </div>
                    <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                      {formatNumber(pageTotals.totalReserved)}
                    </div>
                  </div>

                  {/* Total Available */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Available
                    </div>
                    <div className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                      {formatNumber(pageTotals.totalAvailable)}
                    </div>
                  </div>

                  {/* Value @ Avg Cost */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Value (Avg Cost)
                    </div>
                    <div className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatMoney(pageTotals.valueAtAvgCost)}
                    </div>
                  </div>

                  {/* Value @ Last Cost */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Value (Last Cost)
                    </div>
                    <div className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                      {formatMoney(pageTotals.valueAtLastCost)}
                    </div>
                  </div>

                  {/* In Stock */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      In Stock ({pageTotals.inStockCount})
                    </div>
                    <div className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      {pageTotals.inStockCount}
                    </div>
                  </div>

                  {/* Low Stock */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Low Stock ({pageTotals.lowStockCount})
                    </div>
                    <div className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                      {pageTotals.lowStockCount}
                    </div>
                  </div>

                  {/* Out of Stock */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Out of Stock ({pageTotals.outOfStockCount})
                    </div>
                    <div className="font-mono font-semibold text-red-600 dark:text-red-400">
                      {pageTotals.outOfStockCount}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {loading && (
            <div
              className="p-8 flex items-center justify-center gap-3 text-xs border-t
              bg-slate-50 text-slate-500 border-slate-200
              dark:bg-slate-950/20 dark:text-slate-400 dark:border-white/5"
            >
              <FaSpinner className="animate-spin text-amber-500 text-base" />
              <span>Loading stock...</span>
            </div>
          )}

          {error && (
            <div className="p-6 text-center text-sm text-red-600 dark:text-red-400 border-t border-slate-200 dark:border-white/5">
              {error}
            </div>
          )}

          {!loading && sortedRows.length === 0 && !error && (
            <div className="p-8 text-center text-xs text-slate-500 dark:text-slate-400">
              No matching stock records found.
            </div>
          )}
        </div>

        {/* PAGINATION */}
        <div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Showing{" "}
            <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
              {rows.length}
            </span>{" "}
            of{" "}
            <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
              {totalElements}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 rounded-lg border text-slate-600 disabled:opacity-40
                bg-slate-50 border-slate-200 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <FaChevronLeft className="text-xs" />
            </button>
            <span className="text-xs font-mono text-slate-600 dark:text-slate-300">
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 rounded-lg border text-slate-600 disabled:opacity-40
                bg-slate-50 border-slate-200 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>

          <select
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
            className="rounded-lg px-3 py-2 text-xs border font-semibold
              bg-slate-50 border-slate-200 text-slate-800
              dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}/page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* BATCH MODAL */}
      {batchModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-md sm:p-4">
          <div
            className="relative flex max-h-[95vh] w-full max-w-7xl flex-col overflow-hidden
            rounded-2xl border border-slate-200 bg-white shadow-2xl
            dark:border-white/10 dark:bg-slate-900 sm:rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-white/10 sm:p-5">
              <div>
                <h2 className="text-lg font-black text-slate-900 dark:text-white">
                  {selectedStock?.productName}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Stock Batches
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBatchModalOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-900 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {batchLoading ? (
                <div className="flex items-center justify-center gap-3 p-10 text-xs text-slate-500 dark:text-slate-400">
                  <FaSpinner className="animate-spin text-amber-500" />
                  Loading batches...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] text-left border-collapse">
                    <thead>
                      <tr className="border-b text-[10px] font-bold tracking-wider uppercase bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                        <th className="px-4 py-3">Batch</th>
                        <th className="px-3 py-3">Barcode</th>
                        <th className="px-3 py-3 text-right">Received</th>
                        <th className="px-3 py-3 text-right">Sold</th>
                        <th className="px-3 py-3 text-right">Available</th>
                        <th className="px-3 py-3 text-right">Damaged</th>
                        <th className="px-3 py-3 text-right">Expired</th>
                        <th className="px-3 py-3 text-right">Purchase Cost</th>
                        <th className="px-3 py-3 text-right">Wholesale</th>
                        <th className="px-3 py-3 text-right">Selling</th>
                        <th className="px-3 py-3">Expiry</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-mono text-xs text-slate-600 dark:divide-white/5 dark:text-slate-300">
                      {batchRows.map((b) => (
                        <tr
                          key={b.id}
                          className="hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                        >
                          <td className="px-4 py-3 font-sans font-semibold text-slate-900 dark:text-white">
                            {b.batchNumber}
                          </td>
                          <td className="px-3 py-3">{b.barcode || "-"}</td>
                          <td className="px-3 py-3 text-right">
                            {b.quantityReceived}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.quantitySold}
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-amber-600 dark:text-amber-500">
                            {b.quantityAvailable}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.quantityDamaged}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.quantityExpired}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.purchaseCost}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.wholesalePrice}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {b.sellingPrice}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {b.expiryDate || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 p-3 dark:border-white/10">
              <button
                type="button"
                disabled={batchPage === 0}
                onClick={() => setBatchPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-40
                  bg-slate-50 border-slate-200 text-slate-700
                  dark:bg-white/5 dark:border-white/10 dark:text-slate-300"
              >
                Prev
              </button>
              <span className="text-xs font-mono text-slate-600 dark:text-slate-300">
                Page {batchPage + 1} / {batchTotalPages}
              </span>
              <button
                type="button"
                disabled={batchPage >= batchTotalPages - 1}
                onClick={() => setBatchPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-40
                  bg-slate-50 border-slate-200 text-slate-700
                  dark:bg-white/5 dark:border-white/10 dark:text-slate-300"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md">
          <div
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl space-y-4
            dark:border-white/10 dark:bg-slate-900 sm:rounded-3xl sm:p-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">
                Update Stock
              </h2>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Reorder Level
                </label>
                <input
                  type="number"
                  value={stockForm.reorderLevel}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      reorderLevel: Number(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-amber-500/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Reserved Quantity
                </label>
                <input
                  type="number"
                  value={stockForm.reservedQuantity}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      reservedQuantity: Number(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-amber-500/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            {editError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
                {editError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setEditError("");
                  setEditModalOpen(false);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateStock}
                className="rounded-xl bg-amber-600 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-amber-600/10 hover:bg-amber-500"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
